import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Layout } from './CoreComponents';
import ItemView from './Shared/ItemView';
import BaseGrid from './Shared/BaseGrid';
import { useToast } from '../contexts/ToastContext';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store/index';
import { getPORequests } from '../store/slices/poRequestSlice';
import { getSuppliersWithPurchaseInfo, type SupplierWithPurchaseInfo } from '../store/slices/supplierSlice';
import { createQuoteFromPoRequests } from '../store/slices/quoteSlice';
import { formatDate } from '../utils/formatterDate';
import { type ColDef, type ICellRendererParams, type SelectionChangedEvent } from 'ag-grid-community';
import { PORequestDto } from '../generated';
import { useExpandedBatches } from '../hooks/useExpandedBatches';


const DraftQuotePage: React.FC = () => {
  const { showSuccess, showWarning } = useToast();
  const dispatch = useDispatch<AppDispatch>();
  const { items: requests, loading } = useSelector((state: RootState) => state.poRequest);
  const { suppliersWithPurchaseInfo, loadingWithPurchaseInfo, errorWithPurchaseInfo } = useSelector((state: RootState) => state.supplier);
  const supplierList: SupplierWithPurchaseInfo[] = useMemo(() => suppliersWithPurchaseInfo ?? [], [suppliersWithPurchaseInfo]);
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [selectedRequests, setSelectedRequests] = useState<string[]>([]);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<PORequestDto | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [timeoutDays, setTimeoutDays] = useState(3); // Varsayılan 3 gün
  const [quoteNote, setQuoteNote] = useState(''); // Teklif notu için state
  const [selectedSuppliers, setSelectedSuppliers] = useState<any[]>([]); // Seçili tedarikçiler
  const [showSupplierModal, setShowSupplierModal] = useState(false); // Tedarikçi lookup modal
  const [tempSelectedSuppliers, setTempSelectedSuppliers] = useState<any[]>([]); // Geçici seçimler
  const [editingSupplierEmail, setEditingSupplierEmail] = useState<number | null>(null); // Düzenlenen email
  const [supplierEmailEdits, setSupplierEmailEdits] = useState<{[key: number]: string}>({}); // Email düzenlemeleri
  const [supplierSearchTerm, setSupplierSearchTerm] = useState(''); // Tedarikçi arama terimi
  const [selectedSupplierCategory, setSelectedSupplierCategory] = useState(''); // Seçili tedarikçi kategorisi
  const [showAllSuppliers, setShowAllSuppliers] = useState(true); // true = tüm tedarikçiler, false = sadece hasPurchaseHistory === true
  const [showConfirmationModal, setShowConfirmationModal] = useState(false); // Confirmation modal
  const [confirmationType, setConfirmationType] = useState<'draft' | 'send'>('draft'); // Confirmation türü
  const [showItemViewModal, setShowItemViewModal] = useState(false); // ItemView modal
  const [itemToView, setItemToView] = useState<any>(null); // Görüntülenecek item
  const [expandedBatches, setExpandedBatches] = useExpandedBatches('draftQuote_expandedBatches');

  // Load requests on component mount
  useEffect(() => {
    dispatch(getPORequests({ 
      RequestStatus: 'approved',
      ...(user?.id && { PurchaserId: user.id })
    }));
  }, [dispatch, user?.id]);

  const openQuoteModal = (request: PORequestDto) => {
    setSelectedProduct(request);
    setShowQuoteModal(true);
  };

  const closeQuoteModal = () => {
    setShowQuoteModal(false);
    setSelectedProduct(null);

    setSelectedRequests([]); // Seçili ürünleri temizle
    setTimeoutDays(3); // Timeout günü varsayılana dönsün
    setQuoteNote(''); // Not alanını temizle
    setSelectedSuppliers([]); // Seçili tedarikçileri temizle
  };

  const openSupplierModal = () => {
    setTempSelectedSuppliers([...selectedSuppliers]); // Mevcut seçimleri geçici olarak kopyala
    
    // Seçilen kalemlerin ilk kaleminin kategorisini al
    const selectedProducts = filteredRequests.filter(req => selectedRequests.includes(req.id?.toString() || ''));
    if (selectedProducts.length > 0 && selectedProducts[0]?.categoryName) {
      setSelectedSupplierCategory(selectedProducts[0].categoryName);
    } else {
      setSelectedSupplierCategory(''); // Kategori yoksa tüm kategoriler
    }
    
    setShowSupplierModal(true);
  };

  const closeSupplierModal = () => {
    setShowSupplierModal(false);
    setTempSelectedSuppliers([]);
    setSupplierSearchTerm('');
    setSelectedSupplierCategory('');
    setShowAllSuppliers(true);
  };

  const handleSupplierSelect = () => {
    // Email düzenlemelerini uygula
    const updatedSuppliers = tempSelectedSuppliers.map(supplier => ({
      ...supplier,
      email: supplierEmailEdits[supplier.id] || supplier.email
    }));
    setSelectedSuppliers(updatedSuppliers);
    setShowSupplierModal(false);
    setTempSelectedSuppliers([]);
    setEditingSupplierEmail(null);
    setSupplierEmailEdits({});
  };

  const startEmailEdit = (supplierId: number, currentEmail: string) => {
    setEditingSupplierEmail(supplierId);
    setSupplierEmailEdits(prev => ({
      ...prev,
      [supplierId]: currentEmail || ''
    }));
  };

  const saveEmailEdit = () => {
    setEditingSupplierEmail(null);
  };

  const cancelEmailEdit = (supplierId: number) => {
    setEditingSupplierEmail(null);
    setSupplierEmailEdits(prev => {
      const newEdits = { ...prev };
      delete newEdits[supplierId];
      return newEdits;
    });
  };

  const updateEmailEdit = (supplierId: number, value: string) => {
    setSupplierEmailEdits(prev => ({
      ...prev,
      [supplierId]: value
    }));
  };

  const removeSupplier = (supplierId: number) => {
    setSelectedSuppliers(prev => prev.filter(s => s.id !== supplierId));
  };

  const openItemView = useCallback((item: any) => {
    setItemToView(item);
    setShowItemViewModal(true);
  }, []);

  const closeItemViewModal = () => {
    setShowItemViewModal(false);
    setItemToView(null);
  };


  // Modal açıldığında seçili kalemlerin ItemCode'ları ile tedarikçileri (satın alma bilgili) yükle
  useEffect(() => {
    if (showSupplierModal) {
      const selectedProducts = requests.filter(req => selectedRequests.includes(req.id?.toString() || ''));
      const itemCodes = selectedProducts.map(p => p.itemCode).filter((c): c is string => Boolean(c));
      const firstCode = itemCodes[0] ?? '';
      dispatch(getSuppliersWithPurchaseInfo({ body: firstCode }));
    }
  }, [showSupplierModal, dispatch, selectedRequests, requests]);

  // Endpoint yanıtı geldiğinde: en az 1 tedarikçide hasPurchaseHistory true ise "Tüm tedarikçiler" işaretsiz, yoksa işaretli
  useEffect(() => {
    if (showSupplierModal && !loadingWithPurchaseInfo && supplierList.length > 0) {
      const anyWithHistory = supplierList.some(s => s.hasPurchaseHistory === true);
      setShowAllSuppliers(!anyWithHistory);
    }
  }, [showSupplierModal, loadingWithPurchaseInfo, supplierList]);





  const handleDraftSave = () => {
    setConfirmationType('draft');
    setShowConfirmationModal(true);
  };

  const handleSendQuote = () => {
    setConfirmationType('send');
    setShowConfirmationModal(true);
  };

  const createQuoteOnly = async () => {
    if (timeoutDays <= 0 || !timeoutDays) {
      showWarning('Teklif süresi 1 günden az olamaz! Lütfen geçerli bir süre girin.');
      return;
    }
    
    // Email validation - check if any supplier has empty or invalid email
    const suppliersWithEmptyEmail = selectedSuppliers.filter(supplier => 
      !supplier.email || supplier.email.trim() === ''
    );
    
    if (suppliersWithEmptyEmail.length > 0) {
      showWarning('Tüm tedarikçilerin email adresi doldurulmalıdır! Lütfen eksik email adreslerini tamamlayın.');
      return;
    }
    
    // Seçili tüm ürünleri al
    const selectedProducts = filteredRequests.filter(req => selectedRequests.includes(req.id?.toString() || ''));
    const poRequestIds = selectedProducts.map(product => product.id).filter(Boolean) as number[];
    
    try {
      // Her tedarikçi için ayrı ayrı API çağrısı yap
      const quotePromises = selectedSuppliers.map(supplier => {
        const quoteData = {
          id: 0, // Backend tarafından set edilecek
          poRequestIds,
          supplierId: supplier.id,
          supplierName: supplier.companyName,
          supplierEmail: supplier.email,
          noteToSupplier: quoteNote,
          timeoutDays,
          currency: 'TRY',
          sendMailAutomaticaly: true // Mail gönder
        };
        
        return dispatch(createQuoteFromPoRequests(quoteData));
      });
      
      // Tüm quote oluşturma işlemlerini bekle
      const results = await Promise.all(quotePromises);
      
      // Başarısız işlem var mı kontrol et
      const hasError = results.some((result: any) => {
        // Redux action rejected (HTTP error)
        if (result.error) return true;
        // Backend success false (HTTP 200 but logic error)
        if (result.payload && result.payload.success === false) return true;
        return false;
      });

      if (hasError) {
        // Hata mesajı bulmaya çalış
        const errorResult: any = results.find((r: any) => r.error || (r.payload && r.payload.success === false));
        let errorMessage = 'Bazı teklifler oluşturulurken hata oluştu!';
        
        if (errorResult) {
          if (errorResult.payload?.message) {
            errorMessage = errorResult.payload.message;
          } else if (typeof errorResult.payload === 'string') {
            errorMessage = errorResult.payload;
          } else if (errorResult.error?.message) {
            errorMessage = errorResult.error.message;
          }
        }
        
        showWarning(errorMessage);
        return;
      }
      
      showSuccess(`${selectedProducts.length} ürün için ${selectedSuppliers.length} tedarikçiye ${timeoutDays} gün zaman aşımı ile teklif başarıyla oluşturuldu! Mail gönderimi için 'Teklif Mail Yönetimi' ekranını kullanabilirsiniz.`);
      // Liste güncellenmesi için endpoint çağır
      dispatch(getPORequests({ 
        RequestStatus: 'approved',
        ...(user?.id && { PurchaserId: user.id })
      }));
      closeQuoteModal();
      
    } catch (error) {
      console.error('Create quote error:', error);
      showWarning('Teklif oluşturulurken bir hata oluştu!');
    }
  };

  const confirmAction = async () => {
    if (confirmationType === 'draft') {
      // Sadece teklif oluştur, mail gönderme
      await createQuoteOnly();
    } else {
      // Teklif oluştur ve mail gönder
      await sendQuote();
    }
    setShowConfirmationModal(false);
  };


  const sendQuote = async () => {
    if (timeoutDays <= 0 || !timeoutDays) {
      showWarning('Teklif süresi 1 günden az olamaz! Lütfen geçerli bir süre girin.');
      return;
    }
    
    // Email validation - check if any supplier has empty or invalid email
    const suppliersWithEmptyEmail = selectedSuppliers.filter(supplier => 
      !supplier.email || supplier.email.trim() === ''
    );
    
    if (suppliersWithEmptyEmail.length > 0) {
      showWarning('Tüm tedarikçilerin email adresi doldurulmalıdır! Lütfen eksik email adreslerini tamamlayın.');
      return;
    }
    
    // Seçili tüm ürünleri al
    const selectedProducts = filteredRequests.filter(req => selectedRequests.includes(req.id?.toString() || ''));
    const poRequestIds = selectedProducts.map(product => product.id).filter(Boolean) as number[];
    
    try {
      // Her tedarikçi için ayrı ayrı API çağrısı yap
      const quotePromises = selectedSuppliers.map(supplier => {
        const quoteData = {
          id: 0, // Backend tarafından set edilecek
          poRequestIds,
          supplierId: supplier.id,
          supplierName: supplier.companyName,
          supplierEmail: supplier.email,
          noteToSupplier: quoteNote,
          timeoutDays,
          currency: 'TRY',
          sendMailAutomaticaly: true // Mail gönder
        };
        
        return dispatch(createQuoteFromPoRequests(quoteData));
      });
      
      // Tüm quote oluşturma işlemlerini bekle
      const results = await Promise.all(quotePromises);
      
      // Başarısız işlem var mı kontrol et
      const hasError = results.some((result: any) => {
        // Redux action rejected (HTTP error)
        if (result.error) return true;
        // Backend success false (HTTP 200 but logic error)
        if (result.payload && result.payload.success === false) return true;
        return false;
      });

      if (hasError) {
        // Hata mesajı bulmaya çalış
        const errorResult: any = results.find((r: any) => r.error || (r.payload && r.payload.success === false));
        let errorMessage = 'Teklif gönderilirken bir hata oluştu!';
        
        if (errorResult) {
          if (errorResult.payload?.message) {
            errorMessage = errorResult.payload.message;
          } else if (typeof errorResult.payload === 'string') {
            errorMessage = errorResult.payload;
          } else if (errorResult.error?.message) {
            errorMessage = errorResult.error.message;
          }
        }
        
        showWarning(errorMessage);
        return;
      }
      
      showSuccess(`${selectedProducts.length} ürün için ${selectedSuppliers.length} tedarikçiye ${timeoutDays} gün zaman aşımı ile teklif başarıyla gönderildi!`);
      // Liste güncellenmesi için endpoint çağır
      dispatch(getPORequests({ 
        RequestStatus: 'approved',
        ...(user?.id && { PurchaserId: user.id })
      }));
      closeQuoteModal();
      
    } catch (error) {
      console.error('Send quote error:', error);
      showWarning('Teklif gönderilirken bir hata oluştu!');
    }
  };

  const filteredRequests = requests.filter(request => {
    // Tek arama terimi ile tüm alanlarda arama yap
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchesId = (request.id?.toString() || '').toLowerCase().includes(term);
      const matchesItemCode = (request.itemCode?.toLowerCase() || '').includes(term);
      const matchesRootCode = ((request as any).rootCode?.toLowerCase() || '').includes(term);
      const matchesShortCode = (request.shortCode?.toLowerCase() || '').includes(term);
      const matchesItemName = (request.itemName?.toLowerCase() || '').includes(term);
      
      if (!matchesId && !matchesItemCode && !matchesRootCode && !matchesShortCode && !matchesItemName) {
        return false;
      }
    }
    const matchesGroup = !selectedGroup || request.categoryName === selectedGroup;
    return matchesGroup;
  }).sort((a, b) => {
    // Talep no'ya göre sıralama (desc - büyükten küçüğe)
    const idA = a.id || 0;
    const idB = b.id || 0;
    return idB - idA;
  });

  // Listedeki kategorileri al (benzersiz) ve alfabetik sırala
  const availableCategories = Array.from(
    new Set(requests.map(request => request.categoryName).filter(Boolean))
  ).sort((a, b) => (a || '').localeCompare(b || '', 'tr')) as string[];

  // Group requests by batchId
  const groupedByBatch = filteredRequests.reduce((acc, request) => {
    const batchId = (request as any).batchId || 0;
    if (!acc[batchId]) {
      acc[batchId] = [];
    }
    acc[batchId]!.push(request);
    return acc;
  }, {} as Record<number, typeof filteredRequests>);

  // Toggle batch expansion
  const toggleBatch = (batchId: number) => {
    setExpandedBatches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(batchId)) {
        newSet.delete(batchId);
      } else {
        newSet.add(batchId);
      }
      return newSet;
    });
  };

  // Toggle all batches
  const allBatchIds = Object.keys(groupedByBatch).map(id => parseInt(id));
  const isAllExpanded = allBatchIds.length > 0 && allBatchIds.every(id => expandedBatches.has(id));

  const toggleAllBatches = () => {
    if (isAllExpanded) {
      setExpandedBatches(new Set());
    } else {
      setExpandedBatches(new Set(allBatchIds));
    }
  };

  // Handle column sort
  // Tedarikçi kategorilerini al (benzersiz) ve alfabetik sırala
  const availableSupplierCategories = Array.from(
    new Set(supplierList.flatMap(supplier => 
      supplier.categories?.map(cat => cat.categoryName).filter(Boolean) || []
    ))
  ).sort((a, b) => (a || '').localeCompare(b || '', 'tr')) as string[];

  // Filtrelenmiş tedarikçiler
  const filteredSuppliers = supplierList.filter(supplier => {
    // Arama terimi filtresi
    const matchesSearch = !supplierSearchTerm || 
      (supplier.companyName?.toLowerCase().includes(supplierSearchTerm.toLowerCase()) || '') ||
      (supplier.email?.toLowerCase().includes(supplierSearchTerm.toLowerCase()) || '') ||
      (supplier.categories?.some(cat => 
        cat.categoryName?.toLowerCase().includes(supplierSearchTerm.toLowerCase())
      ) || false);
    
    // Kategori filtresi
    const matchesCategory = !selectedSupplierCategory || 
      supplier.categories?.some(cat => cat.categoryName === selectedSupplierCategory) || false;
    
    return matchesSearch && matchesCategory;
  });

  // Checkbox "Tüm tedarikçiler" kapalıyken sadece satın alma geçmişi olanları göster
  const displayedSuppliers = showAllSuppliers
    ? filteredSuppliers
    : filteredSuppliers.filter(s => s.hasPurchaseHistory);

  // Kategori eşleşmiyorsa (filtrelenmiş tedarikçi yoksa) tüm tedarikçileri yükle
  useEffect(() => {
    if (showSupplierModal && selectedSupplierCategory && filteredSuppliers.length === 0 && supplierList.length > 0) {
      // Kategori seçili ama eşleşen tedarikçi yok, tüm tedarikçileri göster
      setSelectedSupplierCategory('');
    }
  }, [showSupplierModal, selectedSupplierCategory, filteredSuppliers.length, supplierList.length]);

  const childColumnDefs = useMemo<ColDef[]>(() => [
    {
      headerName: '',
      headerCheckboxSelection: true,
      checkboxSelection: true,
      minWidth: 40,
      maxWidth: 40,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
    },
    {
      headerName: 'Talep No',
      field: 'id',
      minWidth: 70,
      flex: 0.5,
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'Ürün Kodu',
      field: 'itemCode',
      minWidth: 100,
      flex: 0.8,
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.value) return '-';
        return (
          <span
            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer underline"
            title={params.value?.startsWith('PRJ') ? 'Proje detayları' : 'Ürün detayları'}
            onClick={(e) => {
              e.stopPropagation();
              openItemView(params.data);
            }}
          >
            {params.value}
          </span>
        );
      },
    },
    {
      headerName: 'Kök Kod',
      valueGetter: (params) => params.data?.rootCode || '-',
      minWidth: 80,
      flex: 0.6,
    },
    {
      headerName: 'Kısa Kod',
      field: 'shortCode',
      minWidth: 70,
      flex: 0.5,
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'Kategori',
      field: 'categoryName',
      minWidth: 120,
      flex: 1,
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'Ürün Adı',
      field: 'itemName',
      minWidth: 180,
      flex: 1.5,
      tooltipField: 'itemName',
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'Teslim Tarihi',
      field: 'requestedDeliveryDate',
      minWidth: 100,
      flex: 0.7,
      valueFormatter: (params) => params.value ? formatDate(params.value) : '-',
    },
    {
      headerName: 'Onay Mikt.',
      field: 'approvedQuantity',
      minWidth: 90,
      flex: 0.6,
      type: 'rightAligned',
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.value) {
          return <span className="bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-1 py-0.5 rounded text-xs">-</span>;
        }
        return (
          <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-1 py-0.5 rounded text-xs">
            {Number(params.value).toLocaleString('tr-TR')} {params.data?.unit || 'Adet'}
          </span>
        );
      },
    },
    {
      headerName: 'Not',
      valueGetter: (params) => params.data?.note ?? params.data?.Note ?? '',
      minWidth: 120,
      flex: 0.8,
      tooltipValueGetter: (params) => params.data?.note ?? params.data?.Note ?? '',
      valueFormatter: (params) => params.value || '-',
    },
  ], [openItemView]);

  const supplierColumnDefs = useMemo<ColDef<SupplierWithPurchaseInfo>[]>(() => [
    {
      headerName: 'Şirket Adı',
      field: 'companyName',
      minWidth: 200,
      flex: 2,
      cellRenderer: (params: ICellRendererParams<SupplierWithPurchaseInfo>) => {
        if (!params.value) return '-';
        return (
          <span className="flex items-center gap-1.5">
            {params.value}
            {params.data?.isLastSupplier === 1 && (
              <span title="En son alınan tedarikçi" className="text-amber-500 dark:text-amber-400">
                <i className="fas fa-star text-xs"></i>
              </span>
            )}
          </span>
        );
      },
    },
    {
      headerName: 'Telefon',
      field: 'phone',
      minWidth: 130,
      flex: 1,
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'Şehir',
      field: 'city',
      minWidth: 100,
      flex: 1,
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'İlçe',
      field: 'country',
      minWidth: 100,
      flex: 1,
      valueFormatter: (params) => params.value || '-',
    },
  ], []);

  const onSupplierSelectionChanged = useCallback((event: SelectionChangedEvent<SupplierWithPurchaseInfo>) => {
    const selectedRows = event.api.getSelectedRows();
    setTempSelectedSuppliers(selectedRows);
  }, []);

  const onChildSelectionChanged = useCallback((event: any, batchRequests: any[]) => {
    const selectedRows = event.api.getSelectedRows();
    const selectedIds = new Set(selectedRows.map((r: any) => r.id?.toString()).filter(Boolean));
    const batchIds = new Set(batchRequests.map((r: any) => r.id?.toString()).filter(Boolean));

    setSelectedRequests(prev => {
      const withoutBatch = prev.filter(id => !batchIds.has(id));
      return [...withoutBatch, ...Array.from(selectedIds)] as string[];
    });
  }, []);

  return (
    <Layout  currentScreen="draft-quote">
      <div className="max-w-8xl mx-auto h-full flex flex-col">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-4 transition-colors duration-300 flex-shrink-0">
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-end gap-3">
            {/* Search bar - dinamik genişlik */}
            <div className="flex-1 min-w-[300px]">
              <input 
                type="text" 
                placeholder="Talep No, Ürün Kodu, Kök Kod, Kısa Kod veya Ürün Adı ile Ara..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 text-sm" 
              />
            </div>
            {/* Filtreler ve Yenile */}
            <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
              <select 
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="">Tüm Gruplar</option>
                {availableCategories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              <button 
                onClick={() => {
                  dispatch(getPORequests({ 
                    RequestStatus: 'approved',
                    ...(user?.id && { PurchaserId: user.id })
                  }));
                }}
                className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <i className="fas fa-sync-alt mr-2"></i>Yenile
              </button>
            </div>
            </div>
            
            {/* Taslak Teklif Oluştur Button Row */}
            <div className="flex justify-end">
              <button 
                onClick={() => {
                  if (selectedRequests.length > 0) {
                    // İlk seçili ürünü modal için set et (çoklu ürün desteği için geliştirilecek)
                    const firstSelectedRequest = filteredRequests.find(req => selectedRequests.includes(req.id?.toString() || ''));
                    if (firstSelectedRequest) {
                      openQuoteModal(firstSelectedRequest);
                    }
                  }
                }}
                disabled={selectedRequests.length === 0}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                <i className="fas fa-paper-plane mr-2"></i>Teklif Talebi Oluştur
              </button>
            </div>
            
          </div>
        </div>


        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors duration-300 flex flex-col max-h-[calc(100vh-300px)]">
          {/* Expandable Grid */}
          {loading ? (
            <div className="px-6 py-12 text-center">
              <div className="flex items-center justify-center space-x-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                <span className="text-gray-600 dark:text-gray-400">Talep verileri yükleniyor...</span>
              </div>
            </div>
          ) : Object.keys(groupedByBatch).length === 0 ? (
            <div className="px-6 py-12 text-center">
              <div className="text-gray-500 dark:text-gray-400">
                <i className="fas fa-inbox text-4xl mb-4"></i>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  {searchTerm || selectedGroup ? 'Arama kriterlerinize uygun talep bulunamadı' : 'Henüz talep bulunmuyor'}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {searchTerm || selectedGroup 
                    ? 'Farklı arama kriterleri deneyebilir veya filtreleri temizleyebilirsiniz.'
                    : 'Yeni talep oluşturmak için ilgili menüyü kullanabilirsiniz.'
                  }
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Mobile Card Layout */}
              <div className="lg:hidden overflow-auto flex-1">
                <div className="p-2 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
                  <span className="text-xs text-gray-500 dark:text-gray-400">{Object.keys(groupedByBatch).length} Parti · {selectedRequests.length} seçili</span>
                  <button
                    onClick={toggleAllBatches}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                  >
                    {isAllExpanded ? 'Hepsini Daralt' : 'Hepsini Genişlet'}
                  </button>
                </div>
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {Object.entries(groupedByBatch).map(([batchIdStr, batchRequests]) => {
                    const batchId = parseInt(batchIdStr);
                    const isExpanded = expandedBatches.has(batchId);
                    const firstRequest = batchRequests[0];
                    const uniqueCategories = Array.from(new Set(batchRequests.map(r => r.categoryName).filter(Boolean))).join(', ');
                    const itemCount = batchRequests.length;
                    const batchSelectedCount = batchRequests.filter(r => selectedRequests.includes(r.id?.toString() || '')).length;

                    if (!firstRequest) return null;

                    return (
                      <div key={batchId}>
                        <div
                          className="px-4 py-3 flex items-center gap-3 bg-gray-50 dark:bg-gray-700/50 cursor-pointer active:bg-gray-100 dark:active:bg-gray-700 transition-colors"
                          onClick={() => toggleBatch(batchId)}
                        >
                          <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} text-xs text-gray-500 dark:text-gray-400 w-4`}></i>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-gray-900 dark:text-white">Parti #{batchId || '-'}</span>
                              <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded-full">{itemCount} Kalem</span>
                              {batchSelectedCount > 0 && (
                                <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-1.5 py-0.5 rounded-full">{batchSelectedCount} seçili</span>
                              )}
                            </div>
                            <p className="text-xs text-blue-600 dark:text-blue-400 truncate mt-0.5">{uniqueCategories || '-'}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-xs font-medium text-gray-700 dark:text-gray-300">{firstRequest.createdByName || `User ${firstRequest.createdBy}`}</div>
                            <div className="text-[10px] text-gray-500 dark:text-gray-400">{firstRequest.createdAt ? formatDate(firstRequest.createdAt) : '-'}</div>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="bg-white dark:bg-gray-800">
                            {batchRequests.map((req) => {
                              const reqId = req.id?.toString() || '';
                              const isSelected = selectedRequests.includes(reqId);
                              return (
                                <div
                                  key={req.id}
                                  className={`px-4 py-3 border-t border-gray-100 dark:border-gray-700/50 ${isSelected ? 'bg-blue-50 dark:bg-blue-900/10' : ''}`}
                                >
                                  <div className="flex items-start gap-3">
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => {
                                        setSelectedRequests(prev =>
                                          isSelected ? prev.filter(id => id !== reqId) : [...prev, reqId]
                                        );
                                      }}
                                      className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-start justify-between mb-1">
                                        <div className="min-w-0 flex-1">
                                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{req.itemName || '-'}</p>
                                          <p className="text-xs text-gray-500 dark:text-gray-400">
                                            <span
                                              className="text-blue-600 dark:text-blue-400 underline cursor-pointer"
                                              onClick={(e) => { e.stopPropagation(); openItemView(req); }}
                                            >{req.itemCode || '-'}</span>
                                            {req.shortCode ? ` · ${req.shortCode}` : ''}
                                          </p>
                                        </div>
                                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400 ml-2 flex-shrink-0">#{req.id}</span>
                                      </div>
                                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                        <div className="flex justify-between">
                                          <span className="text-gray-500 dark:text-gray-400">Kategori:</span>
                                          <span className="text-gray-700 dark:text-gray-300">{req.categoryName || '-'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-500 dark:text-gray-400">Onay Mikt.:</span>
                                          <span className="text-gray-700 dark:text-gray-300">{req.approvedQuantity ? `${Number(req.approvedQuantity).toLocaleString('tr-TR')} ${req.unit || 'Adet'}` : '-'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-500 dark:text-gray-400">Teslim:</span>
                                          <span className="text-gray-700 dark:text-gray-300">{req.requestedDeliveryDate ? formatDate(req.requestedDeliveryDate) : '-'}</span>
                                        </div>
                                        {((req as any).note || (req as any).Note) && (
                                          <div className="flex justify-between col-span-2">
                                            <span className="text-gray-500 dark:text-gray-400">Not:</span>
                                            <span className="text-gray-700 dark:text-gray-300 truncate ml-2">{(req as any).note ?? (req as any).Note}</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Desktop Table + Grid Layout */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full border border-gray-300 dark:border-gray-600" style={{ tableLayout: 'fixed' }}>
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-center text-[11px] font-medium text-gray-700 dark:text-gray-300" style={{ width: '36px' }}>
                        <button 
                          onClick={toggleAllBatches}
                          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors focus:outline-none"
                          title={isAllExpanded ? "Hepsini Daralt" : "Hepsini Genişlet"}
                        >
                          <i className={`fas fa-angle-double-${isAllExpanded ? 'up' : 'down'} text-xs`}></i>
                        </button>
                      </th>
                      <th className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-left text-[11px] font-medium text-gray-700 dark:text-gray-300" style={{ width: '72px' }}>Parti No</th>
                      <th className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-left text-[11px] font-medium text-gray-700 dark:text-gray-300">Özet</th>
                      <th className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-left text-[11px] font-medium text-gray-700 dark:text-gray-300">Oluşturan - Tarih</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(groupedByBatch).map(([batchIdStr, batchRequests]) => {
                      const batchId = parseInt(batchIdStr);
                      const isExpanded = expandedBatches.has(batchId);
                      const firstRequest = batchRequests[0];
                      
                      const uniqueCategories = Array.from(new Set(batchRequests.map(r => r.categoryName).filter(Boolean))).join(', ');
                      const itemCount = batchRequests.length;
                      
                      if (!firstRequest) return null;
                      
                      return (
                        <React.Fragment key={batchId}>
                          {/* Parent Row */}
                          <tr
                            className="bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                            onClick={() => toggleBatch(batchId)}
                          >
                            <td className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-center">
                              <i 
                                className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} text-[10px] text-gray-500 dark:text-gray-400`}
                              ></i>
                            </td>
                            <td className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-xs font-semibold text-gray-800 dark:text-gray-100">
                              {batchId || '-'}
                            </td>
                            <td className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-xs text-gray-800 dark:text-gray-100">
                              <div className="flex flex-col leading-tight">
                                <span className="font-medium text-gray-700 dark:text-gray-200">{uniqueCategories || '-'}</span>
                                <span className="text-[10px] text-gray-500 dark:text-gray-400">{itemCount} Kalem</span>
                              </div>
                            </td>
                            <td className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-xs text-gray-800 dark:text-gray-100">
                              <div className="flex flex-col leading-tight">
                                <div className="font-medium text-gray-700 dark:text-gray-200">{firstRequest.createdByName || `User ${firstRequest.createdBy}`}</div>
                                <div className="text-[10px] text-gray-500 dark:text-gray-400">{firstRequest.createdAt ? formatDate(firstRequest.createdAt) : '-'}</div>
                              </div>
                            </td>
                          </tr>
                          
                          {/* Child Grid - Expanded */}
                          {isExpanded && (
                            <tr>
                              <td colSpan={4} className="border border-gray-300 dark:border-gray-600 p-0">
                                <div className="ag-child-grid">
                                  <BaseGrid
                                    gridStateKey="ag-grid-draft-quote-child"
                                    rowData={batchRequests}
                                    columnDefs={childColumnDefs}
                                    getRowId={(params) => String(params.data.id)}
                                    rowSelection="multiple"
                                    onSelectionChanged={(event) => onChildSelectionChanged(event, batchRequests)}
                                    suppressNoRowsOverlay={true}
                                    autoSizeStrategy={{ type: 'fitGridWidth' }}
                                  />
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Quote Modal */}
      {showQuoteModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl mx-auto my-2 sm:my-4 transition-colors duration-300 relative max-h-[calc(100vh-1rem)] sm:max-h-[calc(100vh-2rem)] lg:max-h-[calc(100vh-2rem)] flex flex-col lg:block">
            {/* Empty Header */}
            <div className="h-8 sm:h-10 flex items-start justify-end px-4 pt-2 sm:pt-3 flex-shrink-0">
              <button 
                onClick={closeQuoteModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 p-2 sm:p-3 rounded-lg transition-colors"
              >
                <i className="fas fa-times text-lg sm:text-xl"></i>
              </button>
            </div>
            
             {/* Content */}
             <div className="p-3 sm:p-4 lg:p-8 overflow-y-auto flex-1 flex flex-col lg:overflow-y-auto lg:max-h-[calc(100vh-6rem)] lg:flex-col">
               <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 flex-1">
                 
                 {/* Left Column - Request Items & Supplier Selection */}
                 <div className="lg:col-span-2 flex flex-col space-y-6">
                  
                  {/* Request Items */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 max-h-48 sm:max-h-56 lg:max-h-64 lg:p-3 flex flex-col">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white">Seçilen Kalemler</h3>
                      <div className="flex items-center space-x-2">
                        <span className="bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full text-xs font-medium">
                          <i className="fas fa-box mr-1"></i>
                          {selectedRequests.length} kalem
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto">
                      <div className="space-y-1">
                        {filteredRequests.filter(req => selectedRequests.includes(req.id?.toString() || '')).map((request) => (
                          <div key={request.id} className="flex items-center justify-between p-1.5 bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                            <div className="flex items-center space-x-1.5">
                              <div className="bg-primary/10 p-1 rounded">
                                <i className="fas fa-box text-primary text-xs"></i>
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                                  <span className="text-blue-600 dark:text-blue-400 font-semibold">#{request.id}</span> - {request.itemCode ? (
                                    <span 
                                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer underline"
                                      onClick={() => openItemView(request)}
                                      title={request.itemCode?.startsWith('PRJ') ? "Proje detayları" : "Ürün detayları"}
                                    >
                                      {request.itemCode}
                                    </span>
                                  ) : (
                                    request.itemCode
                                  )}
                                </p>
                                <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{request.itemName}</p>
                                <div className="flex items-center space-x-2 mt-1">
                                  {request.categoryName && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                                      <i className="fas fa-tag mr-1"></i>
                                      {request.categoryName}
                                    </span>
                                  )}
                                  {request.shortCode && (
                                    <span className="text-xs text-blue-600 dark:text-blue-400 truncate">Kısa Kod: {request.shortCode}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="text-right ml-1">
                              <p className="text-xs font-medium text-gray-900 dark:text-white">{request.approvedQuantity ? Number(request.approvedQuantity).toLocaleString('tr-TR') : '-'}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {request.requestedDeliveryDate ? formatDate(request.requestedDeliveryDate).split(' ')[0] : '-'}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                   {/* Supplier Selection */}
                   <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4 lg:p-6 flex flex-col flex-1">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Tedarikçi Seçimi</h3>
                      <button
                        onClick={openSupplierModal}
                        className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                      >
                        <i className="fas fa-plus mr-2"></i>
                        Tedarikçi Seç
                      </button>
                    </div>
                    
                     <div className="flex-1 space-y-2 lg:space-y-3 overflow-y-auto max-h-60 lg:max-h-80">
                      {selectedSuppliers.length > 0 ? (
                        selectedSuppliers.map((supplier) => {
                          const hasValidEmail = supplier.email && supplier.email.trim() !== '';
                          return (
                            <div key={supplier.id} className={`border border-gray-200 dark:border-gray-600 rounded-lg p-3 lg:p-5 ${
                              hasValidEmail ? 'bg-white dark:bg-gray-800' : 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-600'
                            }`}>
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <h4 className="font-medium text-gray-900 dark:text-white">{supplier.companyName || `Tedarikçi ${supplier.id}`}</h4>
                                  <div className="mt-1">
                                    {editingSupplierEmail === supplier.id ? (
                                      <div className="flex items-center space-x-2">
                                        <input
                                          type="email"
                                          value={supplierEmailEdits[supplier.id || 0] || ''}
                                          onChange={(e) => updateEmailEdit(supplier.id || 0, e.target.value)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              saveEmailEdit();
                                              setSelectedSuppliers(prev => 
                                                prev.map(s => 
                                                  s.id === supplier.id 
                                                    ? { ...s, email: supplierEmailEdits[supplier.id || 0] || s.email }
                                                    : s
                                                )
                                              );
                                            }
                                          }}
                                          className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                          placeholder="Email adresi girin"
                                        />
                                        <button
                                          onClick={() => {
                                            saveEmailEdit();
                                            // Email'i güncelle
                                            setSelectedSuppliers(prev => 
                                              prev.map(s => 
                                                s.id === supplier.id 
                                                  ? { ...s, email: supplierEmailEdits[supplier.id || 0] || s.email }
                                                  : s
                                              )
                                            );
                                          }}
                                          className="text-green-600 hover:text-green-800 text-sm"
                                        >
                                          <i className="fas fa-check"></i>
                                        </button>
                                        <button
                                          onClick={() => cancelEmailEdit(supplier.id || 0)}
                                          className="text-red-600 hover:text-red-800 text-sm"
                                        >
                                          <i className="fas fa-times"></i>
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="flex items-center space-x-2">
                                        <span className={`text-sm ${hasValidEmail ? 'text-gray-600 dark:text-gray-400' : 'text-red-600 dark:text-red-400'}`}>
                                          {supplierEmailEdits[supplier.id || 0] || supplier.email || 'Email adresi gerekli'}
                                          {!hasValidEmail && (
                                            <span className="ml-2">
                                              <i className="fas fa-exclamation-triangle mr-1"></i>
                                              Email gerekli
                                            </span>
                                          )}
                                        </span>
                                        <button
                                          onClick={() => startEmailEdit(supplier.id || 0, supplierEmailEdits[supplier.id || 0] || supplier.email || '')}
                                          className="text-primary hover:text-blue-700 text-sm"
                                        >
                                          <i className="fas fa-edit"></i>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <button
                                  onClick={() => removeSupplier(supplier.id)}
                                  className="text-red-500 hover:text-red-700 ml-2"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </div>
                            </div>
                          );
                        })
                       ) : (
                         <div className="flex items-center justify-center h-full min-h-[12rem]">
                           <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                             <i className="fas fa-users text-4xl mb-4"></i>
                             <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Henüz tedarikçi seçilmedi</h4>
                             <p className="text-sm mb-4">Teklif göndermek için en az bir tedarikçi seçmelisiniz</p>
                             <p className="text-xs text-gray-400 dark:text-gray-500">Yukarıdaki "Tedarikçi Seç" butonuna tıklayarak tedarikçi ekleyebilirsiniz</p>
                           </div>
                         </div>
                       )}
                    </div>

                   
                  </div>
                </div>

                {/* Right Column - Settings */}
                <div className="flex flex-col h-full">
                  
                  {/* Quote Settings */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 mb-4 lg:p-4 lg:mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                        <i className="fas fa-clock text-gray-400 mr-2"></i>
                        Teklif Ayarları
                      </h3>
                    </div>
                    
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Yanıt Süresi
                        </label>
                        <div className="flex items-center space-x-3">
                          <input
                            type="number"
                            min="1"
                            max="365"
                            value={timeoutDays}
                            onChange={(e) => {
                              const value = parseInt(e.target.value);
                              if (value && value > 0) {
                                setTimeoutDays(value);
                              } else if (e.target.value === '') {
                                setTimeoutDays(1);
                              }
                            }}
                            onBlur={(e) => {
                              const value = parseInt(e.target.value);
                              if (!value || value <= 0) {
                                setTimeoutDays(1);
                              }
                            }}
                            onFocus={(e) => {
                              e.target.select();
                            }}
                            onKeyDown={(e) => {
                              // Negatif işaret, e, E, +, - karakterlerini engelle
                              if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
                                e.preventDefault();
                              }
                            }}
                            onInput={(e) => {
                              const target = e.target as HTMLInputElement;
                              if (target.value.startsWith('-') || target.value.startsWith('+')) {
                                target.value = target.value.replace(/^[+-]/, '');
                              }
                            }}
                            className="w-20 px-3 py-2 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center"
                          />
                          <span className="text-base text-gray-600 dark:text-gray-400">gün</span>
                          
                          <div className="flex gap-2 ml-2">
                            {[1, 3, 5].map((days) => (
                              <button
                                key={days}
                                onClick={() => setTimeoutDays(days)}
                                className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                                  timeoutDays === days 
                                    ? 'bg-primary text-white' 
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                              >
                                {days}g
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Supplier Notes - Full Height */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 lg:p-5 flex-1 flex flex-col">
                    <h3 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-white mb-3 lg:mb-4">
                      <i className="fas fa-sticky-note text-gray-400 mr-2"></i>
                      Tedarikçiye Not
                    </h3>
                    
                    <div className="flex-1 flex flex-col">
                      <textarea 
                        value={quoteNote}
                        onChange={(e) => setQuoteNote(e.target.value)}
                        rows={12} 
                        className="flex-1 w-full px-3 py-2 lg:px-4 lg:py-3 text-sm lg:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400" 
                        placeholder="Tedarikçiler için notlar..."
                      />
                    </div>
                  </div>

                </div>
              </div>
            </div>
            
            {/* Fixed Action Buttons - Mobile Only */}
            <div className="lg:hidden bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-3 sm:px-4 py-2 sm:py-3 rounded-b-lg flex-shrink-0">
              <div className="flex flex-col sm:flex-row justify-end gap-2">
                <button 
                  onClick={handleDraftSave}
                  disabled={selectedSuppliers.length === 0 || selectedSuppliers.some(supplier => !supplier.email || supplier.email.trim() === '')}
                  className={`w-full sm:w-auto px-3 py-2 rounded text-xs sm:text-sm font-medium transition-colors ${
                    selectedSuppliers.length === 0 || selectedSuppliers.some(supplier => !supplier.email || supplier.email.trim() === '')
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                  style={{ visibility: 'hidden' }}
                >
                  <i className="fas fa-save mr-1"></i>
                  Taslak Olarak Kaydet
                </button>
                <button 
                  onClick={handleSendQuote}
                  disabled={selectedSuppliers.length === 0 || selectedSuppliers.some(supplier => !supplier.email || supplier.email.trim() === '')}
                  className={`w-full sm:w-auto px-4 py-2 rounded text-xs sm:text-sm font-medium transition-colors ${
                    selectedSuppliers.length === 0 || selectedSuppliers.some(supplier => !supplier.email || supplier.email.trim() === '')
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-primary text-white hover:bg-blue-800'
                  }`}
                >
                  <i className="fas fa-paper-plane mr-1"></i>
                  Kaydet ve Gönder
                </button>
              </div>
            </div>

            {/* Desktop Action Buttons */}
            <div className="hidden lg:block bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-3 sm:py-4 rounded-b-lg flex-shrink-0">
              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                <button 
                  onClick={handleDraftSave}
                  disabled={selectedSuppliers.length === 0 || selectedSuppliers.some(supplier => !supplier.email || supplier.email.trim() === '')}
                  className={`w-full sm:w-auto px-4 py-2 rounded text-sm font-medium transition-colors ${
                    selectedSuppliers.length === 0 || selectedSuppliers.some(supplier => !supplier.email || supplier.email.trim() === '')
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                  style={{ visibility: 'hidden' }}
                >
                  <i className="fas fa-save mr-2"></i>
                  Taslak Olarak Kaydet
                </button>
                <button 
                  onClick={handleSendQuote}
                  disabled={selectedSuppliers.length === 0 || selectedSuppliers.some(supplier => !supplier.email || supplier.email.trim() === '')}
                  className={`w-full sm:w-auto px-6 py-2 rounded text-sm font-medium transition-colors ${
                    selectedSuppliers.length === 0 || selectedSuppliers.some(supplier => !supplier.email || supplier.email.trim() === '')
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-primary text-white hover:bg-blue-800'
                  }`}
                >
                  <i className="fas fa-paper-plane mr-2"></i>
                  Kaydet ve Gönder
                </button>
              </div>
            </div>
          </div>
        </div>
       )}

       {/* Supplier Lookup Modal */}
       {showSupplierModal && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-1 sm:p-4 overflow-y-auto">
           <div className="bg-white dark:bg-gray-800 rounded-lg p-2 sm:p-6 w-full max-w-5xl mx-auto my-2 sm:my-4 max-h-[calc(100vh-1rem)] sm:max-h-[calc(100vh-2rem)] overflow-y-auto transition-colors duration-300">
             <div className="flex justify-between items-center mb-4">
               <h3 className="text-lg font-semibold text-gray-900 dark:text-white transition-colors">
                 Tedarikçi Seçimi
               </h3>
               <button 
                 onClick={closeSupplierModal}
                 className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
               >
                 <i className="fas fa-times text-lg"></i>
               </button>
             </div>
             
             <div className="space-y-4">
               {/* Search and Filter */}
               <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                 <div className="flex flex-col sm:flex-row gap-3">
                   <div className="flex-1 relative">
                     <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                       <i className="fas fa-search text-gray-400 dark:text-gray-500"></i>
                     </div>
                     <input 
                       type="text" 
                       placeholder="Tedarikçi adı, kodu veya kategori ile ara..." 
                       value={supplierSearchTerm}
                       onChange={(e) => setSupplierSearchTerm(e.target.value)}
                       className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                     />
                   </div>
                   <div className="sm:w-48">
                     <select 
                       value={selectedSupplierCategory}
                       onChange={(e) => setSelectedSupplierCategory(e.target.value)}
                       className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                     >
                       <option value="">Tüm Kategoriler</option>
                       {availableSupplierCategories.map(category => (
                         <option key={category} value={category}>{category}</option>
                       ))}
                     </select>
                   </div>
                   <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                     <input 
                       type="checkbox" 
                       checked={showAllSuppliers}
                       onChange={(e) => setShowAllSuppliers(e.target.checked)}
                       className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
                     />
                     <span className="text-sm text-gray-700 dark:text-gray-300">Tüm tedarikçiler</span>
                   </label>
                 </div>
               </div>

               {errorWithPurchaseInfo && (
                 <div className="mb-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
                   {errorWithPurchaseInfo}
                 </div>
               )}

               {/* Supplier Grid */}
               <div className="h-[280px] sm:h-[384px]">
                 <BaseGrid<SupplierWithPurchaseInfo>
                   gridStateKey="ag-grid-supplier-lookup"
                   rowData={displayedSuppliers}
                   columnDefs={supplierColumnDefs}
                   getRowId={(params) => String(params.data.id)}
                   rowSelection={{
                     mode: 'multiRow',
                   }}
                   onSelectionChanged={onSupplierSelectionChanged}
                   loading={loadingWithPurchaseInfo}
                   domLayout="normal"
                   suppressNoRowsOverlay={displayedSuppliers.length === 0 && loadingWithPurchaseInfo}
                   autoSizeStrategy={{ type: 'fitGridWidth' }}
                 />
               </div>

               {/* Action Buttons */}
               <div className="flex justify-between items-center pt-3 border-t border-gray-200 dark:border-gray-700">
                 <div className="text-sm text-gray-600 dark:text-gray-400">
                   {tempSelectedSuppliers.length} tedarikçi seçili
                 </div>
                 <div className="flex space-x-2">
                   <button
                     onClick={closeSupplierModal}
                     className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm"
                   >
                     İptal
                   </button>
                   <button
                     onClick={handleSupplierSelect}
                     className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                   >
                     <i className="fas fa-check mr-1"></i>
                     Seçimi Onayla
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ItemView Modal */}
      {showItemViewModal && itemToView && (
        <ItemView
          isOpen={showItemViewModal}
          onClose={closeItemViewModal}
          itemId={itemToView.itemId}
          mode="view"
        />
      )}

      {/* Confirmation Modal */}
      {showConfirmationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-auto transition-colors duration-300">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                  confirmationType === 'draft' 
                    ? 'bg-yellow-100 dark:bg-yellow-900/30' 
                    : 'bg-blue-100 dark:bg-blue-900/30'
                }`}>
                  <i className={`fas ${
                    confirmationType === 'draft' ? 'fa-save' : 'fa-paper-plane'
                  } text-lg ${
                    confirmationType === 'draft' 
                      ? 'text-yellow-600 dark:text-yellow-400' 
                      : 'text-blue-600 dark:text-blue-400'
                  }`}></i>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {confirmationType === 'draft' ? 'Taslak Olarak Kaydet' : 'Kaydet ve Gönder'}
                  </h3>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {confirmationType === 'draft' 
                    ? 'Teklif oluşturulacak ancak mail gönderilmeyecek. Mail gönderimi için "Teklif Mail Yönetimi" ekranını kullanabilirsiniz.'
                    : 'Teklif oluşturulacak ve seçilen tedarikçilere otomatik mail gönderilecek.'
                  }
                </p>
                
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Seçilen Kalemler:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{selectedRequests.length} adet</span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-gray-600 dark:text-gray-400">Tedarikçiler:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{selectedSuppliers.length} adet</span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-gray-600 dark:text-gray-400">Yanıt Süresi:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{timeoutDays} gün</span>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowConfirmationModal(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm"
                >
                  İptal
                </button>
                <button
                  onClick={confirmAction}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    confirmationType === 'draft'
                      ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                      : 'bg-primary text-white hover:bg-blue-800'
                  }`}
                >
                  {confirmationType === 'draft' ? 'Taslak Olarak Kaydet' : 'Kaydet ve Gönder'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
};

export default DraftQuotePage;
